import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Upload, Loader2, AlertCircle, FileSpreadsheet, X, CheckCircle2, ChevronRight, ChevronLeft, Search, MousePointerClick, Download, Pencil, Ban, RotateCcw, Info } from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import PreviewTable from './PreviewTable';
import type { ColumnDef } from './PreviewTable';
import BulkOperationResultPanel from './BulkOperationResultPanel';
import CapabilityDiffPanel from './CapabilityDiffPanel';
import ManualMatchPopover from './ManualMatchPopover';
import type {
  ClinicMatrixPreviewDto,
  ClinicMatrixMatchDto,
  ClinicMatrixCapabilityExecuteRequest,
  ClinicCodesCapabilityExecuteRequest,
  ClinicCapabilityUpdateItem,
  ClinicJunctionUpdate,
  ClinicListItemUpdate,
  ResolvedFieldChoice,
  BulkOperationResultDto,
  MatchStatus,
  ManualMatchOverride,
  ManualMatchEntityType,
  ServiceTypeChoice,
  InferredServiceType,
} from '../../types/cutover';
import { withXmId } from '@shared/utils/xmId';
import './ClinicMatrixImportTab.css';

type FilterMode = 'all' | 'matched' | 'unmatched' | 'ambiguous' | 'unknownType' | 'exists';

const PAGE_SIZE = 50;

function getStatusBadge(status: MatchStatus, alreadyExists?: boolean, isManualOverride?: boolean) {
  if (alreadyExists) return <span className="cmit-badge cmit-badge-exists">Exists</span>;
  if (isManualOverride) return <span className="cmit-badge cmit-badge-manual">Manual</span>;
  switch (status) {
    case 'Matched':
      return <span className="cmit-badge cmit-badge-matched">Matched</span>;
    case 'NotFound':
      return <span className="cmit-badge cmit-badge-notfound">Not Found</span>;
    case 'Ambiguous':
      return <span className="cmit-badge cmit-badge-ambiguous">Ambiguous</span>;
    case 'NotAttempted':
      return <span className="cmit-badge cmit-badge-notattempted">&mdash;</span>;
    default:
      return null;
  }
}

/** Clinic, customer and location all resolved (auto or manual, not rejected). */
function entitiesMatched(m: ClinicMatrixMatchDto, override?: ManualMatchOverride): boolean {
  // A manual pick always counts; an auto-match counts unless the user rejected it (C3)
  const clinicOk = (override?.clinicId != null)
    || (m.clinicMatchStatus === 'Matched' && !override?.rejectedClinic);
  const customerOk = (override?.customerId != null)
    || ((m.customerMatchStatus === 'Matched' || m.customerMatchStatus === 'Ambiguous') && !override?.rejectedCustomer);
  const hasMultiLoc = override?.customerLocationIds != null && override.customerLocationIds.length > 0;
  const locationOk = (override?.customerLocationId != null) || hasMultiLoc
    || (m.locationMatchStatus === 'Matched' && !override?.rejectedLocation);
  return clinicOk && customerOk && locationOk;
}

/** SLV2-3264 — the inferred type when it is decisive (anything but Unknown), else null. */
function inferredChoice(m: ClinicMatrixMatchDto): ServiceTypeChoice | null {
  const t = m.inferredServiceType;
  return t && t !== 'Unknown' ? t : null;
}

/** SLV2-3264 — the type that will be written for a row: the operator's choice, else the decisive inferred type. */
function effectiveServiceType(m: ClinicMatrixMatchDto, override?: ManualMatchOverride): ServiceTypeChoice | null {
  // SLV2-3377: after a multi-location pick the inference belongs to the original location, so only an explicit choice counts
  return override?.serviceTypeKey ?? (override?.recheckStale ? null : inferredChoice(m));
}

/** SLV2-3264 — matched on all three entities, importable except that nobody has said which type it is. */
function needsServiceTypeChoice(m: ClinicMatrixMatchDto, override?: ManualMatchOverride): boolean {
  return !m.alreadyExists && entitiesMatched(m, override) && effectiveServiceType(m, override) == null;
}

function isFullyMatched(m: ClinicMatrixMatchDto, override?: ManualMatchOverride): boolean {
  // SLV2-3302: a row the sheet marks "do not use" for this client is never imported as a relationship.
  if (m.isDoNotUseForClient) return false;
  // SLV2-3377 (PR #98 review): never importable while the re-check runs or after it failed — every
  // import path (auto-select, selectable count, execute) goes through this predicate.
  if (override?.recheckPending || override?.recheckFailed) return false;
  // SLV2-3264: a row is only importable once its relationship type is known — the matrix never
  // states it, so an Unknown inference has to be resolved by the operator first.
  return entitiesMatched(m, override) && effectiveServiceType(m, override) != null && !m.alreadyExists;
}

/** "Ready" = importable with no unresolved ambiguity (a manual pick, incl. multi-location, resolves it). */
function isReadyRow(m: ClinicMatrixMatchDto, override?: ManualMatchOverride): boolean {
  if (!isFullyMatched(m, override)) return false;
  const hasMultiLoc = override?.customerLocationIds != null && override.customerLocationIds.length > 0;
  if (m.customerMatchStatus === 'Ambiguous' && !override?.customerId) return false;
  if (m.clinicMatchStatus === 'Ambiguous' && !override?.clinicId) return false;
  if (m.locationMatchStatus === 'Ambiguous' && !override?.customerLocationId && !hasMultiLoc) return false;
  return true;
}

const SERVICE_TYPE_LABEL: Record<ServiceTypeChoice, string> = {
  Clinical: 'Clinical Services',
  InjuryMgmt: 'Injury Mgmt',
  Both: 'Both',
};

function getServiceTypeBadge(type: InferredServiceType | null | undefined) {
  switch (type) {
    case 'Clinical':
      return <span className="cmit-badge cmit-badge-type-clinical">CS</span>;
    case 'InjuryMgmt':
      return <span className="cmit-badge cmit-badge-type-injury">IM</span>;
    case 'Both':
      return <span className="cmit-badge cmit-badge-type-both">Both</span>;
    case 'Unknown':
      return <span className="cmit-badge cmit-badge-type-unknown">Unknown</span>;
    default:
      return <span className="cmit-badge cmit-badge-notattempted">&mdash;</span>;
  }
}

function isOverrideEmpty(ov: ManualMatchOverride): boolean {
  return !ov.customerId && !ov.clinicId && !ov.customerLocationId
    && (!ov.customerLocationIds || ov.customerLocationIds.length === 0)
    && !ov.rejectedCustomer && !ov.rejectedClinic && !ov.rejectedLocation
    && !ov.serviceTypeKey;
}

/** Reason the matcher gives for an entity's result (C4), with legacy fallback to the combined notes. */
function matchReason(row: ClinicMatrixMatchDto, entityType: ManualMatchEntityType): string | null {
  const reason =
    entityType === 'customer' ? row.customerMatchReason :
    entityType === 'clinic' ? row.clinicMatchReason :
    row.locationMatchReason;
  return reason ?? row.matchNotes ?? null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


/** Convert a string Yes/No value to a boolean */
function toBoolOrNull(value: string | null): boolean | null {
  if (value === null || value === '') return null;
  const lower = value.toLowerCase();
  if (lower === 'yes' || lower === 'true' || lower === '1') return true;
  if (lower === 'no' || lower === 'false' || lower === '0') return false;
  return null;
}

// --- Session storage persistence ---

// Versioned: previews cached by builds before SLV2-3301 have no targetKind / id arrays on their
// diff fields and cannot be executed safely, so a new key retires them (PR #94 review).
const SESSION_STORAGE_KEY = 'clinic-matrix-preview-v2';

/** Shape of data persisted to sessionStorage */
interface CachedMatrixState {
  preview: ClinicMatrixPreviewDto;
  selectedRows: number[];
  filterMode: FilterMode;
  /** C5 wildcard search text (optional — absent in caches from older builds) */
  searchQuery?: string;
  createOrgLevel: boolean;
  importResult: BulkOperationResultDto | null;
  capabilityResult: BulkOperationResultDto | null;
  clinicCodesResult: BulkOperationResultDto | null;
  /** Manual overrides serialized as [rowNumber, override][] entries */
  manualOverrides: [number, ManualMatchOverride][];
}

function loadCachedState(): CachedMatrixState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedMatrixState;
  } catch {
    return null;
  }
}

export default function ClinicMatrixImportTab() {
  // Restore cached state from sessionStorage on initial mount
  const cachedRef = useRef(loadCachedState());
  const cached = cachedRef.current;

  // State
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [createOrgLevel, setCreateOrgLevel] = useState(cached?.createOrgLevel ?? true);
  const [preview, setPreview] = useState<ClinicMatrixPreviewDto | null>(cached?.preview ?? null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(
    cached ? new Set(cached.selectedRows) : new Set(),
  );
  const [filterMode, setFilterMode] = useState<FilterMode>(cached?.filterMode ?? 'all');
  const [searchQuery, setSearchQuery] = useState(cached?.searchQuery ?? '');
  const [importResult, setImportResult] = useState<BulkOperationResultDto | null>(cached?.importResult ?? null);
  const [capabilityResult, setCapabilityResult] = useState<BulkOperationResultDto | null>(cached?.capabilityResult ?? null);
  const [clinicCodesResult, setClinicCodesResult] = useState<BulkOperationResultDto | null>(cached?.clinicCodesResult ?? null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const recheckTokens = useRef(new Map<number, string>());
  const recheckSeq = useRef(0);
  const [showDeactivationPreview, setShowDeactivationPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['relationships']));
  // SLV2-3302: clinics the workbook says to switch off (red on master / Closed&DNU tab)
  const [selectedDeactivations, setSelectedDeactivations] = useState<Set<number>>(new Set());
  const [deactivationResult, setDeactivationResult] = useState<BulkOperationResultDto | null>(null);
  const [restoredFromSession, setRestoredFromSession] = useState(cached?.preview != null);
  const [isExportingMismatches, setIsExportingMismatches] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manual match override state
  const [manualOverrides, setManualOverrides] = useState<Map<number, ManualMatchOverride>>(
    cached?.manualOverrides ? new Map(cached.manualOverrides) : new Map(),
  );
  const [popoverState, setPopoverState] = useState<{
    rowNumber: number;
    entityType: ManualMatchEntityType;
    anchorEl: HTMLElement;
    initialQuery?: string;
    candidates?: { id: number; name: string }[];
  } | null>(null);

  // Prevent the initial mount (with restored values) from triggering a save
  const isRestoringRef = useRef(cached != null);
  useEffect(() => {
    isRestoringRef.current = false;
  }, []);

  // Keep a ref to the latest state so we can flush it synchronously on unmount
  const stateSnapshotRef = useRef<CachedMatrixState | null>(null);

  // Persist state to sessionStorage (debounced to avoid serializing 10K+ rows on every state change)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildSnapshot = useCallback((): CachedMatrixState => ({
    preview: preview!,
    selectedRows: Array.from(selectedRows),
    filterMode,
    searchQuery,
    createOrgLevel,
    importResult,
    capabilityResult,
    clinicCodesResult,
    manualOverrides: Array.from(manualOverrides.entries()),
  }), [preview, selectedRows, filterMode, searchQuery, createOrgLevel, importResult, capabilityResult, clinicCodesResult, manualOverrides]);

  useEffect(() => {
    if (isRestoringRef.current) return;

    if (!preview) {
      stateSnapshotRef.current = null;
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }

    // Always keep the ref up-to-date so unmount can flush it
    stateSnapshotRef.current = buildSnapshot();

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateSnapshotRef.current));
      } catch {
        // sessionStorage is full (5MB limit) — fail silently
      }
    }, 1500);

    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [preview, buildSnapshot]);

  // Flush to sessionStorage on unmount so tab switches don't lose data
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (stateSnapshotRef.current) {
        try {
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateSnapshotRef.current));
        } catch {
          // sessionStorage is full — fail silently
        }
      }
    };
  }, []);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  /** C3: reject an auto-match — the entity becomes unmatched until the user picks a replacement */
  const rejectMatch = useCallback((rowNumber: number, entityType: ManualMatchEntityType) => {
    setManualOverrides((prev) => {
      const next = new Map(prev);
      const existing = next.get(rowNumber) ?? {};
      const flag =
        entityType === 'customer' ? 'rejectedCustomer' :
        entityType === 'clinic' ? 'rejectedClinic' : 'rejectedLocation';
      next.set(rowNumber, { ...existing, [flag]: true });
      return next;
    });
    // The row is no longer importable — drop it from the selection
    setSelectedRows((prev) => {
      if (!prev.has(rowNumber)) return prev;
      const next = new Set(prev);
      next.delete(rowNumber);
      return next;
    });
  }, []);

  /** C3: undo a rejection — the original auto-match comes back */
  const restoreMatch = useCallback((rowNumber: number, entityType: ManualMatchEntityType) => {
    setManualOverrides((prev) => {
      const next = new Map(prev);
      const existing = next.get(rowNumber);
      if (!existing) return prev;
      const updated = { ...existing };
      switch (entityType) {
        case 'customer': delete updated.rejectedCustomer; break;
        case 'clinic': delete updated.rejectedClinic; break;
        case 'location': delete updated.rejectedLocation; break;
      }
      if (isOverrideEmpty(updated)) next.delete(rowNumber);
      else next.set(rowNumber, updated);
      return next;
    });
  }, []);

  /** Open the manual match popover for a specific row/entity */
  const openMatchPopover = useCallback(
    (rowNumber: number, entityType: ManualMatchEntityType, anchorEl: HTMLElement, initialQuery?: string, candidates?: { id: number; name: string }[]) => {
      setPopoverState({ rowNumber, entityType, anchorEl, initialQuery, candidates });
    },
    [],
  );

  /**
   * SLV2-3377: after the operator re-points a row at another clinic or location, ask the server
   * for that pair's Exists flag and inferred type and write them into the preview row, so the
   * row no longer shows what was computed for the auto-selected pair (QA 2026-09-25).
   */
  const runRecheck = useCallback(
    async (row: ClinicMatrixMatchDto, clinicId: number, customerLocationId: number) => {
      const rowNumber = row.rowNumber;
      // Only the latest pick for a row may apply its answer: a slower earlier response is dropped (PR #100 review).
      const token = `${clinicId}:${customerLocationId}:${++recheckSeq.current}`;
      recheckTokens.current.set(rowNumber, token);
      setManualOverrides((prev) => {
        const next = new Map(prev);
        next.set(rowNumber, { ...(next.get(rowNumber) ?? {}), recheckPending: true, recheckStale: false, recheckFailed: false });
        return next;
      });
      try {
        const res = await cutoverApi.recheckClinicMatrixRow(clinicId, customerLocationId);
        if (recheckTokens.current.get(rowNumber) !== token) return;
        const onFile = res.existingServiceTypes ?? [];
        const updatedRow: ClinicMatrixMatchDto = {
          ...row,
          alreadyExists: res.alreadyExists,
          existingServiceTypes: onFile,
          inferredServiceType: res.inferredServiceType,
          injuryAppointmentCount: res.injuryAppointmentCount,
          clinicalAppointmentCount: res.clinicalAppointmentCount,
          serviceTypeReason: res.serviceTypeReason,
          // The upload's "Already on file: …" note described the old pair — replace it with the re-checked one
          matchNotes: [
            ...(row.matchNotes ?? '').split('; ').filter((n) => n && !n.startsWith('Already on file:')),
            ...(onFile.length > 0 ? [`Already on file: ${onFile.join(', ')}`] : []),
          ].join('; ') || null,
        };
        setPreview((p) => {
          if (!p) return p;
          const matches = p.matches.map((m) => (m.rowNumber === rowNumber
            ? { ...m, ...updatedRow, isClosedOnMaster: m.isClosedOnMaster, isEmergencyRoomOnMaster: m.isEmergencyRoomOnMaster, isDoNotUseForClient: m.isDoNotUseForClient }
            : m));
          return { ...p, matches, alreadyExistsCount: matches.filter((m) => m.alreadyExists).length };
        });
        setManualOverrides((prev) => {
          const next = new Map(prev);
          const ov: ManualMatchOverride = { ...(next.get(rowNumber) ?? {}) };
          delete ov.recheckPending;
          delete ov.recheckStale;
          delete ov.recheckFailed;
          // The operator's type choice was made against the old inference; drop it when the inference moved.
          if (ov.serviceTypeKey && (row.inferredServiceType ?? null) !== (res.inferredServiceType ?? null)) delete ov.serviceTypeKey;
          next.set(rowNumber, ov);
          // Selection follows the re-checked answer: now Exists → deselect; now importable → select (PR #100 review).
          const importable = isFullyMatched(updatedRow, ov);
          setSelectedRows((sel) => {
            if (importable === sel.has(rowNumber)) return sel;
            const s = new Set(sel);
            if (importable) s.add(rowNumber); else s.delete(rowNumber);
            return s;
          });
          return next;
        });
      } catch (error) {
        if (recheckTokens.current.get(rowNumber) !== token) return;
        console.error('Failed to re-check the row after the override:', error);
        setManualOverrides((prev) => {
          const next = new Map(prev);
          next.set(rowNumber, { ...(next.get(rowNumber) ?? {}), recheckPending: false, recheckFailed: true });
          return next;
        });
      }
    },
    [],
  );

  /** Handle manual match selection from the popover */
  const handleManualMatchSelect = useCallback(
    (id: number, name: string) => {
      if (!popoverState) return;
      const { rowNumber, entityType } = popoverState;
      // SLV2-3377: a new clinic or location changes the pair → re-check Exists / inferred type for it
      if (preview && (entityType === 'clinic' || entityType === 'location')) {
        const row = preview.matches.find((m) => m.rowNumber === rowNumber);
        const prevOv = manualOverrides.get(rowNumber);
        const multi = prevOv?.customerLocationIds != null && prevOv.customerLocationIds.length > 0;
        const clinicId = entityType === 'clinic' ? id : (prevOv?.clinicId ?? row?.matchedClinicId ?? null);
        const locationId = entityType === 'location' ? id : (multi ? null : (prevOv?.customerLocationId ?? row?.matchedLocationId ?? null));
        if (row && clinicId && locationId) void runRecheck(row, clinicId, locationId);
      }
      setManualOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowNumber) ?? {};
        // Picking a replacement supersedes any rejection of the same entity (C3)
        switch (entityType) {
          case 'customer': {
            const updated = { ...existing, customerId: id, customerName: name };
            delete updated.rejectedCustomer;
            next.set(rowNumber, updated);
            break;
          }
          case 'clinic': {
            const updated = { ...existing, clinicId: id, clinicName: name };
            delete updated.rejectedClinic;
            next.set(rowNumber, updated);
            break;
          }
          case 'location': {
            const updated = { ...existing, customerLocationId: id, locationName: name };
            delete updated.rejectedLocation;
            // A single pick replaces any earlier multi-location pick
            delete updated.customerLocationIds;
            next.set(rowNumber, updated);
            break;
          }
        }
        return next;
      });
      // Auto-select the row if it becomes fully matched with the new override
      if (preview) {
        const row = preview.matches.find((m) => m.rowNumber === rowNumber);
        if (row) {
          setManualOverrides((latestOverrides) => {
            const ov = latestOverrides.get(rowNumber);
            if (ov && isFullyMatched(row, ov)) {
              setSelectedRows((prev) => {
                if (prev.has(rowNumber)) return prev;
                const next = new Set(prev);
                next.add(rowNumber);
                return next;
              });
            }
            return latestOverrides;
          });
        }
      }
      setPopoverState(null);
    },
    [popoverState, preview, manualOverrides, runRecheck],
  );

  /** Handle multi-location selection from the popover (checkbox mode) */
  const handleMultiLocationSelect = useCallback(
    (selections: { id: number; name: string }[]) => {
      if (!popoverState) return;
      // SLV2-3377: the location picker is a checkbox list whenever the customer is known, so the usual
      // "change this row to another location" arrives here with ONE selection. Treat it as a single
      // location pick so the row is re-checked (Exists + inferred type) for the chosen location.
      if (selections.length === 1) {
        handleManualMatchSelect(selections[0].id, selections[0].name);
        return;
      }
      const { rowNumber } = popoverState;
      setManualOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowNumber) ?? {};
        // Clear any single-location override and set multi-location.
        // SLV2-3377: Exists / inferred type were computed for one location, so flag the row for a manual look.
        const updated: ManualMatchOverride = {
          ...existing,
          customerLocationIds: selections,
          recheckStale: true,
        };
        delete updated.customerLocationId;
        delete updated.locationName;
        delete updated.rejectedLocation;
        delete updated.recheckPending;
        delete updated.recheckFailed;
        next.set(rowNumber, updated);
        return next;
      });
      // Auto-select the row if it becomes fully matched
      if (preview) {
        const row = preview.matches.find((m) => m.rowNumber === rowNumber);
        if (row) {
          setManualOverrides((latestOverrides) => {
            const ov = latestOverrides.get(rowNumber);
            if (ov && isFullyMatched(row, ov)) {
              setSelectedRows((prev) => {
                if (prev.has(rowNumber)) return prev;
                const next = new Set(prev);
                next.add(rowNumber);
                return next;
              });
            }
            return latestOverrides;
          });
        }
      }
      setPopoverState(null);
    },
    [popoverState, preview, handleManualMatchSelect],
  );

  /** Clear a manual override for a specific row/entity */
  const clearManualOverride = useCallback(
    (rowNumber: number, entityType: ManualMatchEntityType) => {
      setManualOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowNumber);
        if (!existing) return prev;
        const updated = { ...existing };
        switch (entityType) {
          case 'customer':
            delete updated.customerId;
            delete updated.customerName;
            break;
          case 'clinic':
            delete updated.clinicId;
            delete updated.clinicName;
            break;
          case 'location':
            delete updated.customerLocationId;
            delete updated.locationName;
            delete updated.customerLocationIds;
            break;
        }
        if (isOverrideEmpty(updated)) {
          next.delete(rowNumber);
        } else {
          next.set(rowNumber, updated);
        }
        return next;
      });
    },
    [],
  );

  // Auto-collapse sections when their execute action completes
  useEffect(() => {
    if (importResult) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete('relationships');
        return next;
      });
    }
  }, [importResult]);

  useEffect(() => {
    if (capabilityResult) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete('capabilities');
        return next;
      });
    }
  }, [capabilityResult]);

  useEffect(() => {
    if (clinicCodesResult) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete('clinicCodes');
        return next;
      });
    }
  }, [clinicCodesResult]);

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: () => cutoverApi.uploadClinicMatrix(file!),
    onSuccess: (result) => {
      setPreview(result);
      setManualOverrides(new Map());
      setPopoverState(null);
      setSelectedRows(new Set());
      setImportResult(null);
      setCapabilityResult(null);
      setClinicCodesResult(null);
      setDeactivationResult(null);
      // Pre-tick every candidate that is still active; already-inactive ones have nothing to do.
      setSelectedDeactivations(new Set((result.deactivationCandidates ?? []).filter((c) => !c.isAlreadyInactive).map((c) => c.clinicId)));
      setFilterMode('all');
      setSearchQuery('');
      setRestoredFromSession(false);
    },
  });

  const executeMutation = useMutation({
    mutationFn: () => {
      const selected = preview!.matches.filter((m) => {
        if (!selectedRows.has(m.rowNumber)) return false;
        const ov = manualOverrides.get(m.rowNumber);
        return isFullyMatched(m, ov);
      });
      // Expand rows: multi-location overrides produce one import item per location
      const items = selected.flatMap((m) => {
        const ov = manualOverrides.get(m.rowNumber);
        const clinicId = ov?.clinicId ?? m.matchedClinicId!;
        const customerId = ov?.customerId ?? m.matchedCustomerId!;
        // isFullyMatched guarantees a type here (SLV2-3264)
        const serviceTypeKey = effectiveServiceType(m, ov)!;

        if (ov?.customerLocationIds && ov.customerLocationIds.length > 0) {
          return ov.customerLocationIds.map((loc) => ({
            clinicId,
            customerLocationId: loc.id,
            customerId,
            serviceTypeKey,
          }));
        }
        return [{
          clinicId,
          customerLocationId: ov?.customerLocationId ?? m.matchedLocationId!,
          customerId,
          serviceTypeKey,
        }];
      });
      return cutoverApi.executeClinicMatrixImport({ items, createOrgLevel });
    },
    onSuccess: (result) => {
      setImportResult(result);
      setShowConfirmModal(false);
    },
  });

  /**
   * SLV2-3301 — one executor for both capability panels. Every diff field carries its target
   * (Column / Capability / Category / Procedure) and the ids to add or remove, resolved by the
   * API, so this only groups the operator's "spreadsheet" choices per clinic and target kind.
   */
  const buildListTargetRequest = useCallback((resolvedChoices: ResolvedFieldChoice[]): ClinicCodesCapabilityExecuteRequest => {
    const columnByClinic = new Map<number, ClinicCapabilityUpdateItem>();
    const capsByClinic = new Map<number, ClinicJunctionUpdate>();
    const categoriesByClinic = new Map<number, ClinicListItemUpdate>();
    const proceduresByClinic = new Map<number, ClinicListItemUpdate>();

    const listEntry = (map: Map<number, ClinicListItemUpdate>, clinicId: number): ClinicListItemUpdate => {
      let entry = map.get(clinicId);
      if (!entry) {
        entry = { clinicId, idsToAdd: [], idsToRemove: [] };
        map.set(clinicId, entry);
      }
      return entry;
    };
    const pushUnique = (target: number[], ids: number[]) => {
      for (const id of ids) if (!target.includes(id)) target.push(id);
    };

    for (const choice of resolvedChoices) {
      // A field without server-resolved target metadata (a preview cached before SLV2-3301) is
      // never applied; the session key change makes this unreachable, but never claim success silently.
      if (!choice.targetKind) {
        throw new Error(`Field '${choice.fieldName}' has no target information. Re-upload the workbook to refresh the preview.`);
      }
      switch (choice.targetKind) {
        case 'Column': {
          // The only column left is MaskTypes; the legacy names are accepted for previews restored from session.
          let item = columnByClinic.get(choice.clinicId);
          if (!item) {
            item = {
              clinicId: choice.clinicId,
              hasPAT: null, fitTestType: null, maskTypes: null, hasXRay: null,
              setHasPAT: false, setFitTestType: false, setMaskTypes: false, setHasXRay: false,
            };
            columnByClinic.set(choice.clinicId, item);
          }
          switch (choice.fieldName) {
            case 'MaskTypes': item.setMaskTypes = true; item.maskTypes = choice.spreadsheetValue; break;
            case 'HasPAT': item.setHasPAT = true; item.hasPAT = toBoolOrNull(choice.spreadsheetValue); break;
            case 'FitTestType': item.setFitTestType = true; item.fitTestType = choice.spreadsheetValue; break;
            case 'HasXRay': item.setHasXRay = true; item.hasXRay = toBoolOrNull(choice.spreadsheetValue); break;
          }
          break;
        }
        case 'Capability': {
          let entry = capsByClinic.get(choice.clinicId);
          if (!entry) {
            entry = { clinicId: choice.clinicId, capabilityTypeIdsToAdd: [], capabilityTypeIdsToRemove: [] };
            capsByClinic.set(choice.clinicId, entry);
          }
          pushUnique(entry.capabilityTypeIdsToAdd, choice.idsToAdd);
          pushUnique(entry.capabilityTypeIdsToRemove, choice.idsToRemove);
          break;
        }
        case 'Category': {
          const entry = listEntry(categoriesByClinic, choice.clinicId);
          pushUnique(entry.idsToAdd, choice.idsToAdd);
          pushUnique(entry.idsToRemove, choice.idsToRemove);
          break;
        }
        case 'Procedure': {
          const entry = listEntry(proceduresByClinic, choice.clinicId);
          pushUnique(entry.idsToAdd, choice.idsToAdd);
          pushUnique(entry.idsToRemove, choice.idsToRemove);
          break;
        }
      }
    }

    const nonEmpty = (u: ClinicListItemUpdate) => u.idsToAdd.length > 0 || u.idsToRemove.length > 0;
    return {
      clinicColumnUpdates: Array.from(columnByClinic.values()).filter(
        (i) => i.setMaskTypes || i.setHasPAT || i.setFitTestType || i.setHasXRay,
      ),
      junctionUpdates: Array.from(capsByClinic.values()).filter(
        (u) => u.capabilityTypeIdsToAdd.length > 0 || u.capabilityTypeIdsToRemove.length > 0,
      ),
      categoryUpdates: Array.from(categoriesByClinic.values()).filter(nonEmpty),
      procedureUpdates: Array.from(proceduresByClinic.values()).filter(nonEmpty),
    };
  }, []);

  // Clinic Matrix sheet capabilities → same executor, own result panel
  const capabilityMutation = useMutation({
    mutationFn: (request: ClinicCodesCapabilityExecuteRequest) =>
      cutoverApi.executeClinicCodesUpdates(request),
    onSuccess: (result) => {
      setCapabilityResult(result);
    },
  });

  const handleCapabilityExecute = useCallback(
    async (_request: ClinicMatrixCapabilityExecuteRequest, resolvedChoices: ResolvedFieldChoice[]) => {
      await capabilityMutation.mutateAsync(buildListTargetRequest(resolvedChoices));
    },
    [capabilityMutation, buildListTargetRequest],
  );

  // SLV2-3302: switch off the confirmed closed / do-not-use clinics through the existing deactivation tool
  const deactivationMutation = useMutation({
    mutationFn: (clinicIds: number[]) =>
      cutoverApi.executeDeactivation({
        clinicIds,
        triggerWords: ['Clinic Matrix import: red on master sheet / Closed&DNU tab (SLV2-3302)'],
      }),
    onSuccess: (result) => {
      setDeactivationResult(result);
    },
  });

  const toggleDeactivation = useCallback((clinicId: number) => {
    setSelectedDeactivations((prev) => {
      const next = new Set(prev);
      if (next.has(clinicId)) next.delete(clinicId); else next.add(clinicId);
      return next;
    });
  }, []);

  // SLV2-3379 (Morgan 2026-09-25): show what will be switched off before anything runs
  const handleDeactivate = useCallback(() => {
    if (selectedDeactivations.size === 0) return;
    setShowDeactivationPreview(true);
  }, [selectedDeactivations]);

  const confirmDeactivation = useCallback(() => {
    const ids = Array.from(selectedDeactivations);
    setShowDeactivationPreview(false);
    if (ids.length > 0) deactivationMutation.mutate(ids);
  }, [selectedDeactivations, deactivationMutation]);

  // Clinic Codes sheet capabilities
  const clinicCodesMutation = useMutation({
    mutationFn: (request: ClinicCodesCapabilityExecuteRequest) =>
      cutoverApi.executeClinicCodesUpdates(request),
    onSuccess: (result) => {
      setClinicCodesResult(result);
    },
  });

  const handleClinicCodesExecute = useCallback(
    async (_request: ClinicMatrixCapabilityExecuteRequest, resolvedChoices: ResolvedFieldChoice[]) => {
      await clinicCodesMutation.mutateAsync(buildListTargetRequest(resolvedChoices));
    },
    [clinicCodesMutation, buildListTargetRequest],
  );

  // File handling
  const handleFile = useCallback((f: File) => {
    if (f.name.endsWith('.xlsx') || f.name.endsWith('.xls')) {
      setFile(f);
      setPreview(null);
      setImportResult(null);
      setCapabilityResult(null);
      setClinicCodesResult(null);
      setManualOverrides(new Map());
      setPopoverState(null);
      setRestoredFromSession(false);
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
    },
    [handleFile],
  );

  // Cutover C8: blank template with the exact sheet/column layout the upload expects
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await cutoverApi.downloadClinicMatrixTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ClinicMatrix_Import_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, []);

  const handleExportMismatches = useCallback(async () => {
    if (!file) return;
    setIsExportingMismatches(true);
    try {
      const blob = await cutoverApi.exportMismatches(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ClinicMatrix_Mismatches_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export mismatches:', err);
    } finally {
      setIsExportingMismatches(false);
    }
  }, [file]);

  // Count manual overrides that made rows "Ready" (fully matched, no unresolved ambiguity)
  const manuallyResolvedCount = useMemo(() => {
    if (!preview) return 0;
    let count = 0;
    for (const m of preview.matches) {
      const ov = manualOverrides.get(m.rowNumber);
      if (!ov || !isFullyMatched(m, ov)) continue;
      // Was it already "Ready" without the override? If not, it was manually resolved.
      const wasReady = isFullyMatched(m) &&
        m.customerMatchStatus !== 'Ambiguous' &&
        m.clinicMatchStatus !== 'Ambiguous' &&
        m.locationMatchStatus !== 'Ambiguous';
      if (!wasReady) count++;
    }
    return count;
  }, [preview, manualOverrides]);

  // SLV2-3264: Ready is computed client-side because the server's matchedCount does not know
  // about Unknown types or operator choices; both change what is importable.
  const readyCount = useMemo(() => {
    if (!preview) return 0;
    let count = 0;
    for (const m of preview.matches) {
      if (isReadyRow(m, manualOverrides.get(m.rowNumber))) count++;
    }
    return count;
  }, [preview, manualOverrides]);

  const unknownTypeCount = useMemo(() => {
    if (!preview) return 0;
    let count = 0;
    for (const m of preview.matches) {
      if (needsServiceTypeChoice(m, manualOverrides.get(m.rowNumber))) count++;
    }
    return count;
  }, [preview, manualOverrides]);

  // SLV2-3264: operator picks (or clears) the relationship type for a row
  const setServiceTypeChoice = useCallback((rowNumber: number, choice: ServiceTypeChoice | '') => {
    setManualOverrides((prev) => {
      const next = new Map(prev);
      const updated: ManualMatchOverride = { ...(next.get(rowNumber) ?? {}) };
      if (choice === '') delete updated.serviceTypeKey;
      else updated.serviceTypeKey = choice;
      if (isOverrideEmpty(updated)) next.delete(rowNumber);
      else next.set(rowNumber, updated);
      return next;
    });
  }, []);

  // SLV2-3264: hand the undecidable rows to whoever classifies them by hand (Briley)
  const exportUnknownTypeCsv = useCallback(() => {
    if (!preview) return;
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = [
      'Row', 'Client', 'City', 'State', 'Clinic Code', 'Spreadsheet Clinic',
      'Matched Customer', 'Matched Location', 'Matched Clinic',
      'Injury Appts', 'Clinical Appts', 'Reason', 'Decision (Clinical / InjuryMgmt / Both)',
    ];
    const lines = preview.matches
      .filter((m) => needsServiceTypeChoice(m, manualOverrides.get(m.rowNumber)))
      .map((m) => {
        const ov = manualOverrides.get(m.rowNumber);
        return [
          m.rowNumber, m.clientName, m.city, m.state, m.clinicCode, m.spreadsheetClinicName,
          ov?.customerName ?? m.matchedCustomerName, ov?.locationName ?? m.matchedLocationName, ov?.clinicName ?? m.matchedClinicName,
          m.injuryAppointmentCount ?? 0, m.clinicalAppointmentCount ?? 0, m.serviceTypeReason ?? '', '',
        ].map(esc).join(',');
      });
    const csv = [header.map(esc).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clinic-matrix-unknown-type-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Let the browser start the download before revoking the URL
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [preview, manualOverrides]);

  // Filtered data
  const filteredMatches = useMemo(() => {
    if (!preview) return [];
    // C5: wildcard search — any fragment of client, clinic, code, city, or matched names
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = (m: ClinicMatrixMatchDto): boolean => {
      if (!q) return true;
      const ov = manualOverrides.get(m.rowNumber);
      return [
        m.clientName,
        m.clinicCode,
        m.spreadsheetClinicName,
        m.city,
        m.state,
        m.matchedClinicName,
        m.matchedCustomerName,
        m.matchedCustomerXmId,
        m.matchedLocationName,
        m.matchedLocationXmId,
        ov?.customerName,
        ov?.clinicName,
        ov?.locationName,
      ].some((v) => v != null && v.toLowerCase().includes(q));
    };
    const bySearch = preview.matches.filter(matchesSearch);
    switch (filterMode) {
      case 'matched':
        // "Ready" = fully matched with NO ambiguity (or manually resolved)
        return bySearch.filter((m) => isReadyRow(m, manualOverrides.get(m.rowNumber)));
      case 'unknownType':
        // SLV2-3264: matched but SourceLink history says nothing about the relationship type
        return bySearch.filter((m) => needsServiceTypeChoice(m, manualOverrides.get(m.rowNumber)));
      case 'unmatched':
        return bySearch.filter(
          (m) => {
            if (m.alreadyExists) return false;
            const ov = manualOverrides.get(m.rowNumber);
            const hasMultiLoc = ov?.customerLocationIds != null && ov.customerLocationIds.length > 0;
            return (
              (m.clinicMatchStatus === 'NotFound' && !ov?.clinicId) ||
              (m.customerMatchStatus === 'NotFound' && !ov?.customerId) ||
              (m.locationMatchStatus === 'NotFound' && !ov?.customerLocationId && !hasMultiLoc) ||
              // C3: rejected matches count as unmatched until re-resolved
              (ov?.rejectedClinic === true && !ov?.clinicId) ||
              (ov?.rejectedCustomer === true && !ov?.customerId) ||
              (ov?.rejectedLocation === true && !ov?.customerLocationId && !hasMultiLoc)
            );
          },
        );
      case 'ambiguous':
        return bySearch.filter(
          (m) => {
            if (m.alreadyExists) return false;
            const ov = manualOverrides.get(m.rowNumber);
            const hasMultiLoc = ov?.customerLocationIds != null && ov.customerLocationIds.length > 0;
            return (
              (m.clinicMatchStatus === 'Ambiguous' && !ov?.clinicId) ||
              (m.customerMatchStatus === 'Ambiguous' && !ov?.customerId) ||
              (m.locationMatchStatus === 'Ambiguous' && !ov?.customerLocationId && !hasMultiLoc)
            );
          },
        );
      case 'exists':
        return bySearch.filter((m) => m.alreadyExists);
      default:
        return bySearch;
    }
  }, [preview, filterMode, searchQuery, manualOverrides]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterMode, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredMatches.length / PAGE_SIZE));
  const paginatedMatches = useMemo(
    () => filteredMatches.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredMatches, currentPage],
  );

  /** Render a resolve/pick button for a match cell that is NotFound or Ambiguous */
  const renderResolveBtn = useCallback(
    (rowNumber: number, entityType: ManualMatchEntityType, status: MatchStatus, row: ClinicMatrixMatchDto) => {
      if (status !== 'NotFound' && status !== 'Ambiguous') return null;
      const ov = manualOverrides.get(rowNumber);
      const hasOverride =
        (entityType === 'customer' && ov?.customerId != null) ||
        (entityType === 'clinic' && ov?.clinicId != null) ||
        (entityType === 'location' && ov?.customerLocationId != null);
      if (hasOverride) return null; // already resolved

      // For ambiguous matches, pre-fill the popover with the spreadsheet value so
      // the user sees a list of candidates immediately without typing
      let initialQuery: string | undefined;
      let candidates: { id: number; name: string; detail?: string }[] | undefined;
      if (status === 'Ambiguous') {
        switch (entityType) {
          case 'customer':
            initialQuery = row.clientName;
            candidates = row.customerMatchCandidates?.map(c => ({ id: c.id, name: withXmId(c.name, c.xmId), detail: c.accountNumber ?? undefined }));
            break;
          case 'clinic':
            initialQuery = row.spreadsheetClinicName || row.clinicCode;
            break;
          case 'location':
            initialQuery = [row.city, row.state].filter(Boolean).join(', ');
            break;
        }
      }

      const hasCandidates = candidates != null && candidates.length > 0;

      return (
        <button
          type="button"
          className="cmit-resolve-btn"
          title={status === 'Ambiguous' ? 'Pick correct match' : 'Search and resolve'}
          onClick={(e) => {
            e.stopPropagation();
            openMatchPopover(rowNumber, entityType, e.currentTarget, initialQuery, candidates);
          }}
        >
          {status === 'Ambiguous' ? <MousePointerClick size={12} /> : <Search size={12} />}
          {status === 'Ambiguous'
            ? (hasCandidates ? `Pick (${candidates!.length})` : 'Pick')
            : 'Resolve'}
        </button>
      );
    },
    [manualOverrides, openMatchPopover],
  );

  /** Render a manual override indicator with clear button */
  const renderManualOverride = useCallback(
    (rowNumber: number, entityType: ManualMatchEntityType, name: string) => (
      <div className="cmit-manual-match">
        <span className="cmit-badge cmit-badge-manual">Manual</span>
        <span className="cmit-manual-match-name">{name}</span>
        <button
          type="button"
          className="cmit-manual-match-clear"
          title="Clear override"
          onClick={(e) => {
            e.stopPropagation();
            clearManualOverride(rowNumber, entityType);
          }}
        >
          <X size={10} />
        </button>
      </div>
    ),
    [clearManualOverride],
  );

  /** C3+C4: quiet matched line with why-tooltip plus Change / Reject actions */
  const renderMatchedInline = useCallback(
    (
      row: ClinicMatrixMatchDto,
      entityType: ManualMatchEntityType,
      displayName: string,
      initialQuery?: string,
      candidates?: { id: number; name: string }[],
    ) => {
      const reason = matchReason(row, entityType);
      return (
        <div className="cmit-matched-inline" title={reason ? `${displayName}\nWhy: ${reason}` : displayName}>
          <CheckCircle2 size={13} />
          <span>{displayName}</span>
          {reason && <Info size={12} className="cmit-why-icon" aria-label="Match reason" />}
          <span className="cmit-cell-actions">
            <button
              type="button"
              className="cmit-cell-action"
              title="Change this match"
              aria-label={`Change ${entityType} match`}
              onClick={(e) => {
                e.stopPropagation();
                openMatchPopover(row.rowNumber, entityType, e.currentTarget, initialQuery, candidates);
              }}
            >
              <Pencil size={11} />
            </button>
            <button
              type="button"
              className="cmit-cell-action cmit-cell-action-danger"
              title="Reject this match — not the same thing"
              aria-label={`Reject ${entityType} match`}
              onClick={(e) => {
                e.stopPropagation();
                rejectMatch(row.rowNumber, entityType);
              }}
            >
              <Ban size={11} />
            </button>
          </span>
        </div>
      );
    },
    [openMatchPopover, rejectMatch],
  );

  /** C3: rejected-match state — resolve a replacement or undo */
  const renderRejected = useCallback(
    (
      row: ClinicMatrixMatchDto,
      entityType: ManualMatchEntityType,
      initialQuery?: string,
      candidates?: { id: number; name: string }[],
    ) => (
      <div className="cmit-match-cell">
        <span className="cmit-badge cmit-badge-rejected">Rejected</span>
        <button
          type="button"
          className="cmit-resolve-btn"
          title="Search for the correct match"
          onClick={(e) => {
            e.stopPropagation();
            openMatchPopover(row.rowNumber, entityType, e.currentTarget, initialQuery, candidates);
          }}
        >
          <Search size={12} />
          Resolve
        </button>
        <button
          type="button"
          className="cmit-cell-action"
          title="Undo — restore the original match"
          aria-label={`Undo ${entityType} rejection`}
          onClick={(e) => {
            e.stopPropagation();
            restoreMatch(row.rowNumber, entityType);
          }}
        >
          <RotateCcw size={11} />
        </button>
      </div>
    ),
    [openMatchPopover, restoreMatch],
  );

  // Table columns
  const columns = useMemo<ColumnDef<ClinicMatrixMatchDto>[]>(
    () => [
      {
        key: 'rowNumber',
        header: 'Row',
        sortable: true,
        width: '60px',
      },
      {
        key: 'clientName',
        header: 'Client',
        sortable: true,
        render: (_val, row) => {
          const ov = manualOverrides.get(row.rowNumber);
          const custCandidates = row.customerMatchCandidates?.map(c => ({ id: c.id, name: withXmId(c.name, c.xmId), detail: c.accountNumber ?? undefined }));
          let statusLine: React.ReactNode;
          if (ov?.customerId != null) {
            statusLine = renderManualOverride(row.rowNumber, 'customer', ov.customerName ?? `#${ov.customerId}`);
          } else if (ov?.rejectedCustomer) {
            statusLine = renderRejected(row, 'customer', row.clientName, custCandidates);
          } else if (row.customerMatchStatus === 'Ambiguous' && row.matchedCustomerId != null) {
            // Ambiguous: API auto-selected first match — show it with Pick + Reject
            const custReason = matchReason(row, 'customer');
            statusLine = (
              <div className="cmit-match-cell">
                <div className="cmit-auto-match" title={custReason ? `Auto-selected: ${withXmId(row.matchedCustomerName, row.matchedCustomerXmId)}\nWhy: ${custReason}` : undefined}>
                  <span className="cmit-badge cmit-badge-ambiguous">Auto</span>
                  <span className="cmit-auto-match-name">
                    {withXmId(row.matchedCustomerName, row.matchedCustomerXmId)}
                  </span>
                  {custReason && <Info size={12} className="cmit-why-icon" aria-label="Match reason" />}
                  <button
                    type="button"
                    className="cmit-resolve-btn"
                    title="Change customer selection"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMatchPopover(row.rowNumber, 'customer', e.currentTarget, row.clientName, custCandidates);
                    }}
                  >
                    <MousePointerClick size={12} />
                    {custCandidates && custCandidates.length > 0
                      ? `Pick (${custCandidates.length})`
                      : 'Change'}
                  </button>
                  <button
                    type="button"
                    className="cmit-cell-action cmit-cell-action-danger"
                    title="Reject — none of these is the right customer"
                    aria-label="Reject customer match"
                    onClick={(e) => {
                      e.stopPropagation();
                      rejectMatch(row.rowNumber, 'customer');
                    }}
                  >
                    <Ban size={11} />
                  </button>
                </div>
              </div>
            );
          } else if (row.customerMatchStatus === 'Matched') {
            statusLine = renderMatchedInline(
              row,
              'customer',
              row.matchedCustomerName && row.matchedCustomerName !== row.clientName
                ? withXmId(row.matchedCustomerName, row.matchedCustomerXmId)
                : 'Matched',
              row.clientName,
              custCandidates,
            );
          } else {
            statusLine = (
              <div className="cmit-match-cell">
                {getStatusBadge(row.customerMatchStatus)}
                {renderResolveBtn(row.rowNumber, 'customer', row.customerMatchStatus, row)}
              </div>
            );
          }
          return (
            <div>
              <div className="cmit-cell-primary">{row.clientName}</div>
              {statusLine}
            </div>
          );
        },
      },
      {
        key: 'clinicCode',
        header: 'Clinic',
        sortable: true,
        width: '180px',
        render: (_val, row) => {
          const ov = manualOverrides.get(row.rowNumber);
          let statusLine: React.ReactNode;
          if (ov?.clinicId != null) {
            statusLine = renderManualOverride(row.rowNumber, 'clinic', ov.clinicName ?? `#${ov.clinicId}`);
          } else if (ov?.rejectedClinic) {
            statusLine = renderRejected(row, 'clinic', row.spreadsheetClinicName || row.clinicCode);
          } else if (row.clinicMatchStatus === 'Matched') {
            statusLine = renderMatchedInline(
              row,
              'clinic',
              row.matchedClinicName ?? 'Matched',
              row.spreadsheetClinicName || row.clinicCode,
            );
          } else {
            statusLine = (
              <div className="cmit-match-cell">
                {getStatusBadge(row.clinicMatchStatus)}
                {renderResolveBtn(row.rowNumber, 'clinic', row.clinicMatchStatus, row)}
              </div>
            );
          }
          return (
            <div>
              <div className="cmit-cell-primary">
                {row.clinicCode}
                {/* SLV2-3302: what the sheet's colour / notes say about this clinic */}
                {row.isClosedOnMaster && (
                  <span className="cmit-badge cmit-badge-closed" title="Red on the master sheet: closed for everyone — listed under Closed & Do Not Use">Closed</span>
                )}
                {row.isEmergencyRoomOnMaster && (
                  <span className="cmit-badge cmit-badge-er" title="Pink on the master sheet: Emergency Room category">ER</span>
                )}
                {row.isDoNotUseForClient && (
                  <span className="cmit-badge cmit-badge-dnu" title="Marked do not use for this client — relationship will not be imported">DNU</span>
                )}
                {/* SLV2-3642: the code only matches an inactive (duplicate) clinic — pick the active clinic first */}
                {row.clinicIsInactive && manualOverrides.get(row.rowNumber)?.clinicId == null && (
                  <span className="cmit-badge cmit-badge-closed" title={row.clinicMatchReason ?? 'This clinic is marked Inactive. Pick the active clinic, or pick this one to confirm it.'}>Inactive</span>
                )}
              </div>
              {statusLine}
            </div>
          );
        },
      },
      {
        key: 'city',
        header: 'Location',
        sortable: true,
        render: (_val, row) => {
          const ov = manualOverrides.get(row.rowNumber);
          const multiLocs = ov?.customerLocationIds;
          let statusLine: React.ReactNode;
          if (multiLocs && multiLocs.length > 0) {
            statusLine = (
              <div className="cmit-manual-match">
                <span className="cmit-badge cmit-badge-manual">
                  {multiLocs.length === 1 ? multiLocs[0].name : `${multiLocs.length} locations`}
                </span>
                <button
                  type="button"
                  className="cmit-resolve-btn"
                  title={multiLocs.map((l) => l.name).join(', ')}
                  onClick={(e) => {
                    e.stopPropagation();
                    openMatchPopover(row.rowNumber, 'location', e.currentTarget);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="cmit-manual-match-clear"
                  title="Clear override"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearManualOverride(row.rowNumber, 'location');
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            );
          } else if (ov?.customerLocationId != null) {
            statusLine = renderManualOverride(row.rowNumber, 'location', ov.locationName ?? `#${ov.customerLocationId}`);
          } else if (ov?.rejectedLocation) {
            statusLine = renderRejected(row, 'location');
          } else if (row.locationMatchStatus === 'Matched') {
            statusLine = renderMatchedInline(
              row,
              'location',
              withXmId(row.matchedLocationName, row.matchedLocationXmId) || 'Matched',
            );
          } else if (row.locationMatchStatus === 'NotFound' && (ov?.customerId ?? row.matchedCustomerId) != null) {
            // NotFound location with a known customer: prominent picker scoped to that customer
            statusLine = (
              <div className="cmit-match-cell">
                <button
                  type="button"
                  className="cmit-resolve-btn cmit-resolve-btn-prominent"
                  title="Pick locations for this customer"
                  onClick={(e) => {
                    e.stopPropagation();
                    openMatchPopover(row.rowNumber, 'location', e.currentTarget);
                  }}
                >
                  <Search size={12} />
                  Pick Locations
                </button>
              </div>
            );
          } else {
            statusLine = (
              <div className="cmit-match-cell">
                {getStatusBadge(row.locationMatchStatus)}
                {renderResolveBtn(row.rowNumber, 'location', row.locationMatchStatus, row)}
              </div>
            );
          }
          return (
            <div>
              <div className="cmit-cell-primary">
                {row.city}
                {row.state ? `, ${row.state}` : ''}
              </div>
              {statusLine}
            </div>
          );
        },
      },
      {
        // SLV2-3264: relationship type inferred from SourceLink appointment history, operator can override
        key: 'inferredServiceType',
        header: 'Type',
        width: '175px',
        render: (_val, row) => {
          const ov = manualOverrides.get(row.rowNumber);
          const inferred = inferredChoice(row);
          const needsChoice = needsServiceTypeChoice(row, ov);
          const onFile = row.existingServiceTypes ?? [];
          const showSelect = !row.alreadyExists && entitiesMatched(row, ov);
          return (
            <div className="cmit-type-cell">
              <div className="cmit-type-inferred" title={row.serviceTypeReason ?? undefined}>
                {getServiceTypeBadge(row.inferredServiceType)}
                {row.serviceTypeReason && <Info size={12} className="cmit-why-icon" aria-label="Why this type" />}
                {ov?.serviceTypeKey && <span className="cmit-badge cmit-badge-manual">Manual</span>}
                {ov?.recheckPending && <span className="cmit-badge cmit-badge-review">Re-checking…</span>}
                {ov?.recheckStale && !ov.recheckPending && !ov.serviceTypeKey && (
                  <span className="cmit-badge cmit-badge-ambiguous" title="The inferred type was computed for the original location. Choose the type for the selected locations to import this row.">
                    Choose type for these locations
                  </span>
                )}
                {ov?.recheckFailed && !ov.recheckPending && (
                  <span className="cmit-badge cmit-badge-ambiguous" title="Could not re-check this row. Pick the clinic or location again to retry; the row cannot be imported until then.">
                    Re-check failed
                  </span>
                )}
              </div>
              {showSelect && (
                <select
                  className={`cmit-type-select ${needsChoice ? 'cmit-type-select-needed' : ''}`}
                  value={ov?.serviceTypeKey ?? ''}
                  aria-label="Relationship type to import"
                  title={needsChoice ? 'No history found — choose the relationship type to import this row' : 'Override the inferred relationship type'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setServiceTypeChoice(row.rowNumber, e.target.value as ServiceTypeChoice | '')}
                >
                  <option value="">
                    {inferred ? `Use inferred (${SERVICE_TYPE_LABEL[inferred]})` : 'Choose type…'}
                  </option>
                  <option value="Clinical">Clinical Services</option>
                  <option value="InjuryMgmt">Injury Mgmt</option>
                  <option value="Both">Both</option>
                </select>
              )}
              {onFile.length > 0 && (
                <span className="cmit-type-onfile">On file: {onFile.join(', ')}</span>
              )}
            </div>
          );
        },
      },
      {
        key: 'alreadyExists',
        header: 'Status',
        width: '100px',
        render: (_val, row) => {
          if (row.isDoNotUseForClient) return <span className="cmit-badge cmit-badge-dnu">Do Not Use</span>;
          if (row.alreadyExists) return <span className="cmit-badge cmit-badge-exists">Exists</span>;
          const ov = manualOverrides.get(row.rowNumber);
          if (ov?.recheckPending) return <span className="cmit-badge cmit-badge-review">Re-checking…</span>;
          if (isFullyMatched(row, ov))
            return <span className="cmit-badge cmit-badge-ready">Ready</span>;
          if (needsServiceTypeChoice(row, ov))
            return <span className="cmit-badge cmit-badge-type-unknown">Type?</span>;
          return <span className="cmit-badge cmit-badge-review">Review</span>;
        },
      },
      {
        key: 'matchNotes',
        header: 'Notes',
        render: (val) =>
          val ? <span className="cmit-match-detail">{String(val)}</span> : '\u2014',
      },
    ],
    [manualOverrides, renderResolveBtn, renderManualOverride, renderMatchedInline, renderRejected, rejectMatch, clearManualOverride, openMatchPopover, setServiceTypeChoice],
  );

  const isAnyLoading = uploadMutation.isPending || executeMutation.isPending || capabilityMutation.isPending || clinicCodesMutation.isPending;
  const error = uploadMutation.error || executeMutation.error || capabilityMutation.error || clinicCodesMutation.error;

  // Count selected that are actually importable (including manually resolved)
  // Count total import items (multi-location rows expand to multiple items)
  const selectableCount = preview
    ? preview.matches.reduce((count, m) => {
        if (!selectedRows.has(m.rowNumber)) return count;
        const ov = manualOverrides.get(m.rowNumber);
        if (!isFullyMatched(m, ov)) return count;
        if (ov?.customerLocationIds && ov.customerLocationIds.length > 0) {
          return count + ov.customerLocationIds.length;
        }
        return count + 1;
      }, 0)
    : 0;

  return (
    <div className="cmit">
      {/* Upload Section */}
      {!preview && (
        <div className="cmit-upload-section">
          {/* Dropzone */}
          {!file ? (
            <div
              className={`cmit-dropzone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={32} className="cmit-dropzone-icon" />
              <div className="cmit-dropzone-text">
                Drag and drop your Clinic Matrix file here, or{' '}
                <strong>browse</strong>
              </div>
              <div className="cmit-dropzone-hint">Supports .xlsx files</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="cmit-hidden-file-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFile(e.target.files[0]);
                }}
              />
            </div>
          ) : (
            <div className="cmit-file-info">
              <FileSpreadsheet size={20} color="#16a34a" />
              <span className="cmit-file-name">{file.name}</span>
              <span className="cmit-file-size">{formatFileSize(file.size)}</span>
              <button
                type="button"
                className="cmit-file-remove"
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* Options */}
          <div className="cmit-options-row">
            <label className="cmit-checkbox-label">
              <input
                type="checkbox"
                checked={createOrgLevel}
                onChange={(e) => setCreateOrgLevel(e.target.checked)}
              />
              Also create organization-level entries
            </label>
            <div className="cmit-options-spacer" />
            <button
              type="button"
              className="cmit-btn cmit-btn-secondary"
              title="Blank Excel with the exact sheet names and column headers the import expects"
              onClick={() => { void handleDownloadTemplate(); }}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate ? (
                <Loader2 size={14} className="cmit-spin" />
              ) : (
                <Download size={14} />
              )}
              Download Template
            </button>
            <button
              type="button"
              className="cmit-btn cmit-btn-primary"
              onClick={() => uploadMutation.mutate()}
              disabled={!file || isAnyLoading}
            >
              {uploadMutation.isPending ? (
                <Loader2 size={14} className="cmit-spin" />
              ) : (
                <Upload size={14} />
              )}
              Upload &amp; Match
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="cmit-error">
          <AlertCircle size={16} />
          <span>{String(error)}</span>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <>
          {/* Restored from session indicator */}
          {restoredFromSession && (
            <div className="cmit-file-info">
              <FileSpreadsheet size={20} color="#16a34a" />
              <span className="cmit-file-name">Restored from previous session</span>
            </div>
          )}

          {/* Header Validation Warnings */}
          {preview.headerValidation?.warnings?.length > 0 && (
            <div className="cmit-header-warning">
              <AlertCircle size={16} />
              <div>
                <strong>Header Warnings:</strong>
                <ul>
                  {preview.headerValidation.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="cmit-summary">
            <div className="cmit-summary-item">
              <span className="cmit-summary-count total">{preview.totalRows}</span>
              Total Rows
            </div>
            <div className="cmit-summary-item">
              <CheckCircle2 size={14} color="#16a34a" />
              <span className="cmit-summary-count matched">
                {readyCount}
              </span>
              Ready
              {manuallyResolvedCount > 0 && (
                <span className="cmit-summary-manual-note">
                  ({manuallyResolvedCount} manual)
                </span>
              )}
            </div>
            {unknownTypeCount > 0 && (
              <div className="cmit-summary-item" title="Matched, but SourceLink has no appointment history to say Injury Mgmt vs Clinical Services — pick a type per row or export for manual review">
                <AlertCircle size={14} color="#b91c1c" />
                <span className="cmit-summary-count unmatched">{unknownTypeCount}</span>
                Unknown Type
              </div>
            )}
            <div className="cmit-summary-item">
              <AlertCircle size={14} color="#dc2626" />
              <span className="cmit-summary-count unmatched">{preview.unmatchedCount}</span>
              Unmatched
            </div>
            <div className="cmit-summary-item">
              <AlertCircle size={14} color="#d97706" />
              <span className="cmit-summary-count ambiguous">{preview.ambiguousCount}</span>
              Ambiguous
            </div>
            <div className="cmit-summary-item">
              <span className="cmit-summary-count exists">{preview.alreadyExistsCount}</span>
              Already Exists
            </div>
            {(preview.deactivationCandidates?.length ?? 0) > 0 && (
              <div className="cmit-summary-item" title="Red on the master sheet or listed on the Closed&DNU tab — see the Closed & Do Not Use section">
                <Ban size={14} color="#b91c1c" />
                <span className="cmit-summary-count unmatched">{preview.deactivationCandidates!.length}</span>
                To Deactivate
              </div>
            )}
            {preview.matches.some((m) => m.isDoNotUseForClient) && (
              <div className="cmit-summary-item" title="Rows the sheet marks do not use for that client — not imported as relationships">
                <Ban size={14} color="#b45309" />
                <span className="cmit-summary-count ambiguous">{preview.matches.filter((m) => m.isDoNotUseForClient).length}</span>
                Client DNU
              </div>
            )}
            {preview.capabilityDiffCount > 0 && (
              <div className="cmit-summary-item">
                <span className="cmit-summary-count capability">{preview.capabilityDiffCount}</span>
                Capability Diffs
              </div>
            )}
            {preview.autoAcceptedCount > 0 && (
              <div className="cmit-summary-item">
                <CheckCircle2 size={14} color="#065f46" />
                <span className="cmit-summary-count auto-accepted">{preview.autoAcceptedCount}</span>
                Auto-accepted
              </div>
            )}
            {preview.unrecognizedValueCount > 0 && (
              <div className="cmit-summary-item">
                <AlertCircle size={14} color="#dc2626" />
                <span className="cmit-summary-count unrecognized">{preview.unrecognizedValueCount}</span>
                Unrecognized
              </div>
            )}
            {preview.clinicCodesPreview && (
              <>
                <div className="cmit-summary-divider" />
                <div className="cmit-summary-item">
                  <span className="cmit-summary-count total">{preview.clinicCodesPreview.totalRows}</span>
                  Clinic Codes Rows
                </div>
                <div className="cmit-summary-item">
                  <CheckCircle2 size={14} color="#16a34a" />
                  <span className="cmit-summary-count matched">{preview.clinicCodesPreview.matchedCount}</span>
                  Codes Matched
                </div>
                {(preview.clinicCodesPreview.inactiveClinicCount ?? 0) > 0 && (
                  <div className="cmit-summary-item" title="These Clinic Codes rows match only an inactive clinic, so their values are not applied.">
                    <span className="cmit-summary-count unmatched">{preview.clinicCodesPreview.inactiveClinicCount}</span>
                    Codes on Inactive Clinics (skipped)
                  </div>
                )}
                {preview.clinicCodesPreview.diffCount > 0 && (
                  <div className="cmit-summary-item">
                    <span className="cmit-summary-count capability">{preview.clinicCodesPreview.diffCount}</span>
                    Codes Diffs
                  </div>
                )}
                {preview.clinicCodesPreview.autoAcceptedCount > 0 && (
                  <div className="cmit-summary-item">
                    <CheckCircle2 size={14} color="#065f46" />
                    <span className="cmit-summary-count auto-accepted">{preview.clinicCodesPreview.autoAcceptedCount}</span>
                    Auto-accepted
                  </div>
                )}
              </>
            )}
            {(preview.unmatchedCount > 0 || preview.ambiguousCount > 0) && (
              <button
                type="button"
                className="cmit-export-mismatches-btn"
                onClick={handleExportMismatches}
                disabled={isExportingMismatches}
              >
                {isExportingMismatches ? <Loader2 size={14} className="spinning" /> : <Download size={14} />}
                Export Mismatches
              </button>
            )}
          </div>

          {/* Section 1: Clinic Codes (shown when Clinic Codes sheet exists) */}
          {preview.clinicCodesPreview && preview.clinicCodesPreview.capabilityDiffs.length > 0 && (
            <div className="cmit-section">
              <div
                className="cmit-section-header"
                onClick={() => toggleSection('clinicCodes')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('clinicCodes'); } }}
                aria-expanded={expandedSections.has('clinicCodes')}
              >
                <ChevronRight
                  size={16}
                  className={`cmit-section-chevron ${expandedSections.has('clinicCodes') ? 'expanded' : ''}`}
                />
                <h3 className="cmit-section-title">
                  Clinic Codes Sheet
                  <span className="cmit-section-subtitle">
                    ({preview.clinicCodesPreview.matchedCount} of {preview.clinicCodesPreview.totalRows} clinics matched)
                  </span>
                </h3>
                <div className="cmit-section-badges">
                  {clinicCodesResult ? (
                    <span className="cmit-section-badge cmit-section-badge-success">
                      <CheckCircle2 size={12} />
                      Updates Complete &mdash; {clinicCodesResult.successCount} succeeded
                      {clinicCodesResult.failureCount > 0 && `, ${clinicCodesResult.failureCount} failed`}
                    </span>
                  ) : (
                    <span className="cmit-section-badge cmit-section-badge-info">
                      {preview.clinicCodesPreview.diffCount} Codes Diffs
                    </span>
                  )}
                </div>
              </div>
              {expandedSections.has('clinicCodes') && (
                <div className="cmit-section-body">
                  {!clinicCodesResult ? (
                    <>
                      {/* Header warnings for Clinic Codes sheet */}
                      {preview.clinicCodesPreview.headerValidation?.warnings?.length > 0 && (
                        <div className="cmit-header-warning">
                          <AlertCircle size={16} />
                          <div>
                            <strong>Clinic Codes Header Warnings:</strong>
                            <ul>
                              {preview.clinicCodesPreview.headerValidation.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      <CapabilityDiffPanel
                        diffs={preview.clinicCodesPreview.capabilityDiffs}
                        onExecute={() => {/* handled by onExecuteWithChoices */}}
                        onExecuteWithChoices={handleClinicCodesExecute}
                        isExecuting={clinicCodesMutation.isPending}
                        title="Clinic Codes Capabilities"
                      />
                    </>
                  ) : (
                    <BulkOperationResultPanel result={clinicCodesResult} title="Clinic Codes Capabilities Complete" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section 2: Capabilities */}
          {preview.capabilityDiffs?.length > 0 && (
            <div className="cmit-section">
              <div
                className="cmit-section-header"
                onClick={() => toggleSection('capabilities')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('capabilities'); } }}
                aria-expanded={expandedSections.has('capabilities')}
              >
                <ChevronRight
                  size={16}
                  className={`cmit-section-chevron ${expandedSections.has('capabilities') ? 'expanded' : ''}`}
                />
                <h3 className="cmit-section-title">Capabilities</h3>
                <div className="cmit-section-badges">
                  {capabilityResult ? (
                    <span className="cmit-section-badge cmit-section-badge-success">
                      <CheckCircle2 size={12} />
                      Updates Complete &mdash; {capabilityResult.successCount} succeeded
                      {capabilityResult.failureCount > 0 && `, ${capabilityResult.failureCount} failed`}
                    </span>
                  ) : (
                    <span className="cmit-section-badge cmit-section-badge-info">
                      {preview.capabilityDiffCount} Capability Diffs
                    </span>
                  )}
                </div>
              </div>
              {expandedSections.has('capabilities') && (
                <div className="cmit-section-body">
                  {!capabilityResult ? (
                    <CapabilityDiffPanel
                      diffs={preview.capabilityDiffs}
                      onExecute={() => {/* handled by onExecuteWithChoices */}}
                      onExecuteWithChoices={handleCapabilityExecute}
                      isExecuting={capabilityMutation.isPending}
                    />
                  ) : (
                    <BulkOperationResultPanel result={capabilityResult} title="Capability Updates Complete" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section 2b: Closed & Do Not Use (SLV2-3302) */}
          {(preview.deactivationCandidates?.length ?? 0) > 0 && (
            <div className="cmit-section">
              <div
                className="cmit-section-header"
                onClick={() => toggleSection('closedDnu')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('closedDnu'); } }}
                aria-expanded={expandedSections.has('closedDnu')}
              >
                <ChevronRight
                  size={16}
                  className={`cmit-section-chevron ${expandedSections.has('closedDnu') ? 'expanded' : ''}`}
                />
                <h3 className="cmit-section-title">
                  Closed &amp; Do Not Use
                  <span className="cmit-section-subtitle">
                    ({preview.deactivationCandidates!.length} clinic{preview.deactivationCandidates!.length === 1 ? '' : 's'} to switch off
                    {preview.closedDnuPreview ? `; Closed&DNU tab: ${preview.closedDnuPreview.matchedCount} of ${preview.closedDnuPreview.totalRows} rows matched` : ''})
                  </span>
                </h3>
                <div className="cmit-section-badges">
                  {deactivationResult ? (
                    <span className="cmit-section-badge cmit-section-badge-success">
                      <CheckCircle2 size={12} />
                      Deactivation Complete &mdash; {deactivationResult.successCount} deactivated
                      {deactivationResult.skippedCount > 0 && `, ${deactivationResult.skippedCount} already inactive`}
                      {deactivationResult.failureCount > 0 && `, ${deactivationResult.failureCount} failed`}
                    </span>
                  ) : (
                    <span className="cmit-section-badge cmit-section-badge-info">
                      {selectedDeactivations.size} selected
                    </span>
                  )}
                </div>
              </div>
              {expandedSections.has('closedDnu') && (
                <div className="cmit-section-body">
                  {!deactivationResult ? (
                    <>
                      <p className="cmit-dnu-intro">
                        A clinic name filled <strong>red</strong> on the master sheet means the clinic is closed for everyone.
                        Every row on the <strong>Closed&amp;DNU Clinics</strong> tab means nobody should use that clinic any more.
                        Both switch the clinic off for all clients (it can be re-activated from its profile).
                        Client-specific &ldquo;do not use&rdquo; notes on the master sheet are handled separately: those rows are simply not imported.
                      </p>
                      <div className="cmit-dnu-table-wrap">
                        <table className="cmit-dnu-table">
                          <thead>
                            <tr>
                              <th />
                              <th>Clinic</th>
                              <th>Code</th>
                              <th>Said by</th>
                              <th>Rows</th>
                              <th>Now</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.deactivationCandidates!.map((c) => (
                              <tr key={c.clinicId} className={c.isAlreadyInactive ? 'cmit-dnu-row-inactive' : ''}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedDeactivations.has(c.clinicId)}
                                    disabled={c.isAlreadyInactive}
                                    onChange={() => toggleDeactivation(c.clinicId)}
                                    aria-label={`Deactivate ${c.clinicName}`}
                                  />
                                </td>
                                <td>{c.clinicName}</td>
                                <td>{c.clinicCode ?? '—'}</td>
                                <td>{c.sources.join(', ')}</td>
                                <td>{c.sourceRowNumbers.length > 6 ? `${c.sourceRowNumbers.slice(0, 6).join(', ')} +${c.sourceRowNumbers.length - 6}` : c.sourceRowNumbers.join(', ')}</td>
                                <td>
                                  {c.isAlreadyInactive
                                    ? <span className="cmit-badge cmit-badge-exists">Already inactive</span>
                                    : <span className="cmit-badge cmit-badge-closed">Active</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {preview.closedDnuPreview && preview.closedDnuPreview.unmatchedCount > 0 && (
                        <p className="cmit-dnu-unmatched">
                          {preview.closedDnuPreview.unmatchedCount} row{preview.closedDnuPreview.unmatchedCount === 1 ? '' : 's'} on the Closed&amp;DNU tab did not match a clinic code:{' '}
                          {preview.closedDnuPreview.rows.filter((r) => r.matchedClinicId == null).slice(0, 15).map((r) => `${r.clinicCode || '(blank)'} (${r.company})`).join(', ')}
                          {preview.closedDnuPreview.unmatchedCount > 15 ? ', …' : ''}
                        </p>
                      )}
                      <div className="cmit-dnu-actions">
                        <button
                          type="button"
                          className="cmit-btn cmit-btn-danger"
                          disabled={selectedDeactivations.size === 0 || deactivationMutation.isPending}
                          onClick={handleDeactivate}
                        >
                          {deactivationMutation.isPending ? <Loader2 size={14} className="spinning" /> : <Ban size={14} />}
                          Deactivate {selectedDeactivations.size} clinic{selectedDeactivations.size === 1 ? '' : 's'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <BulkOperationResultPanel result={deactivationResult} title="Clinic Deactivation Complete" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section 3: Relationships */}
          <div className="cmit-section">
            <div
              className="cmit-section-header"
              onClick={() => toggleSection('relationships')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('relationships'); } }}
              aria-expanded={expandedSections.has('relationships')}
            >
              <ChevronRight
                size={16}
                className={`cmit-section-chevron ${expandedSections.has('relationships') ? 'expanded' : ''}`}
              />
              <h3 className="cmit-section-title">Relationships</h3>
              <div className="cmit-section-badges">
                {importResult ? (
                  <span className="cmit-section-badge cmit-section-badge-success">
                    <CheckCircle2 size={12} />
                    Import Complete &mdash; {importResult.successCount} succeeded
                    {importResult.failureCount > 0 && `, ${importResult.failureCount} failed`}
                  </span>
                ) : (
                  <>
                    <span className="cmit-section-badge cmit-section-badge-matched">
                      {readyCount} Ready
                      {manuallyResolvedCount > 0 && ` (${manuallyResolvedCount} manual)`}
                    </span>
                    <span className="cmit-section-badge cmit-section-badge-unmatched">{preview.unmatchedCount} Unmatched</span>
                    {unknownTypeCount > 0 && (
                      <span className="cmit-section-badge cmit-section-badge-unmatched">{unknownTypeCount} Unknown Type</span>
                    )}
                    {preview.ambiguousCount > 0 && (
                      <span className="cmit-section-badge cmit-section-badge-ambiguous">{preview.ambiguousCount} Ambiguous</span>
                    )}
                  </>
                )}
              </div>
            </div>
            {expandedSections.has('relationships') && (
              <div className="cmit-section-body">
                {!importResult ? (
                  <>
                    {/* Filters + wildcard search (C5) */}
                    <div className="cmit-results-header">
                      <div className="cmit-filters">
                        {(['all', 'matched', 'unmatched', 'ambiguous', 'unknownType', 'exists'] as FilterMode[]).map((f) => (
                          <button
                            key={f}
                            type="button"
                            className={`cmit-filter-btn ${filterMode === f ? 'active' : ''}`}
                            onClick={() => setFilterMode(f)}
                          >
                            {f === 'all' ? `All (${preview.totalRows})` :
                             f === 'matched' ? `Ready (${readyCount})` :
                             f === 'unmatched' ? `Unmatched (${preview.unmatchedCount})` :
                             f === 'ambiguous' ? `Ambiguous (${preview.ambiguousCount})` :
                             f === 'unknownType' ? `Unknown Type (${unknownTypeCount})` :
                             `Exists (${preview.alreadyExistsCount})`}
                          </button>
                        ))}
                        {unknownTypeCount > 0 && (
                          <button
                            type="button"
                            className="cmit-filter-btn"
                            title="Download the rows with no inferable relationship type for manual classification"
                            onClick={exportUnknownTypeCsv}
                          >
                            <Download size={12} />
                            Export Unknown ({unknownTypeCount})
                          </button>
                        )}
                      </div>
                      <div className="cmit-search-wrap">
                        <div className="cmit-search">
                          <Search size={14} className="cmit-search-icon" />
                          <input
                            type="text"
                            className="cmit-search-input"
                            placeholder="Search client, clinic, code, city&hellip;"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              className="cmit-search-clear"
                              aria-label="Clear search"
                              onClick={() => setSearchQuery('')}
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        {/* Always rendered so the row height never changes — no layout shift while typing */}
                        <span className="cmit-search-count" aria-live="polite">
                          {searchQuery.trim()
                            ? `${filteredMatches.length} row${filteredMatches.length === 1 ? '' : 's'} match`
                            : ' '}
                        </span>
                      </div>
                    </div>

                    {/* Preview Table */}
                    <PreviewTable<ClinicMatrixMatchDto>
                      data={paginatedMatches}
                      columns={columns}
                      selectedIds={selectedRows}
                      onSelectionChange={setSelectedRows}
                      idField="rowNumber"
                      emptyMessage="No rows match the current filter"
                    />

                    {/* Bottom pagination */}
                    {totalPages > 1 && (
                      <div className="cmit-pagination-bar">
                        <span className="cmit-pagination-info">
                          Showing {(currentPage - 1) * PAGE_SIZE + 1}&ndash;{Math.min(currentPage * PAGE_SIZE, filteredMatches.length)} of {filteredMatches.length} rows
                        </span>
                        <div className="cmit-pagination">
                          <button
                            type="button"
                            className="cmit-pagination-btn"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                          >
                            First
                          </button>
                          <button
                            type="button"
                            className="cmit-pagination-btn"
                            aria-label="Previous page"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeft size={15} />
                          </button>
                          <span className="cmit-pagination-info">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            type="button"
                            className="cmit-pagination-btn"
                            aria-label="Next page"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          >
                            <ChevronRight size={15} />
                          </button>
                          <button
                            type="button"
                            className="cmit-pagination-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <BulkOperationResultPanel result={importResult} title="Relationship Import Complete" />
                )}
              </div>
            )}
          </div>

          {/* Action Bar */}
          <div className="cmit-action-bar">
            <button
              type="button"
              className="cmit-btn cmit-btn-secondary"
              onClick={() => {
                setPreview(null);
                setSelectedRows(new Set());
                setFile(null);
                setImportResult(null);
                setCapabilityResult(null);
                setClinicCodesResult(null);
                setManualOverrides(new Map());
                setPopoverState(null);
                setSearchQuery('');
                setRestoredFromSession(false);
                stateSnapshotRef.current = null;
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Start Over
            </button>
            {!importResult && selectableCount > 0 && (
              <button
                type="button"
                className="cmit-btn cmit-btn-success"
                onClick={() => setShowConfirmModal(true)}
                disabled={selectableCount === 0 || isAnyLoading}
              >
                {executeMutation.isPending ? (
                  <Loader2 size={14} className="cmit-spin" />
                ) : null}
                Import Selected ({selectableCount})
              </button>
            )}
          </div>
        </>
      )}

      {/* SLV2-3379: deactivation preview — nothing changes until the operator confirms here */}
      {preview && (() => {
        const candidates = preview.deactivationCandidates ?? [];
        const chosen = candidates.filter((c) => selectedDeactivations.has(c.clinicId));
        const skipped = candidates.filter((c) => c.isAlreadyInactive).length;
        const n = chosen.length;
        return (
          <ConfirmationModal
            isOpen={showDeactivationPreview}
            title="Preview: clinics to deactivate"
            message={
              `${n} clinic${n === 1 ? '' : 's'} will be switched off for every client. They stop appearing in the matrix and proximity searches; each can be re-activated from its profile.` +
              (skipped > 0 ? ` ${skipped} already-inactive clinic${skipped === 1 ? ' is' : 's are'} skipped.` : '')
            }
            confirmText={`Deactivate ${n} clinic${n === 1 ? '' : 's'}`}
            cancelText="Cancel"
            danger
            loading={deactivationMutation.isPending}
            onConfirm={confirmDeactivation}
            onCancel={() => setShowDeactivationPreview(false)}
          >
            <div className="cmit-dnu-table-wrap" style={{ maxHeight: 320, overflowY: 'auto', marginTop: 12 }}>
              <table className="cmit-dnu-table">
                <thead>
                  <tr>
                    <th>Clinic</th>
                    <th>Code</th>
                    <th>Said by</th>
                  </tr>
                </thead>
                <tbody>
                  {chosen.map((c) => (
                    <tr key={c.clinicId}>
                      <td>{c.clinicName}</td>
                      <td>{c.clinicCode ?? '—'}</td>
                      <td>{c.sources.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ConfirmationModal>
        );
      })()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Import Clinic Matrix"
        message={
          `Import ${selectableCount} clinic-location relationship${selectableCount !== 1 ? 's' : ''}?` +
          (manuallyResolvedCount > 0 ? ` (${manuallyResolvedCount} manually resolved)` : '') +
          '\n\nThis will create rows in CustomerLocationClinics, each with its inferred or chosen type (Clinical Services, Injury Mgmt, or one row of each for Both).' +
          (createOrgLevel
            ? '\n\nOrganization-level entries will also be created in CustomerPreferredClinic.'
            : '')
        }
        confirmText="Import"
        cancelText="Cancel"
        onConfirm={() => executeMutation.mutate()}
        onCancel={() => setShowConfirmModal(false)}
        loading={executeMutation.isPending}
      />

      {/* Manual Match Popover */}
      {popoverState && (
        <ManualMatchPopover
          entityType={popoverState.entityType}
          customerId={
            popoverState.entityType === 'location'
              ? (() => {
                  const ov = manualOverrides.get(popoverState.rowNumber);
                  const row = preview?.matches.find((m) => m.rowNumber === popoverState.rowNumber);
                  return ov?.customerId ?? row?.matchedCustomerId ?? undefined;
                })()
              : undefined
          }
          onSelect={handleManualMatchSelect}
          onMultiSelect={popoverState.entityType === 'location' ? handleMultiLocationSelect : undefined}
          onClose={() => setPopoverState(null)}
          anchorEl={popoverState.anchorEl}
          initialQuery={popoverState.initialQuery}
          preselectedIds={
            popoverState.entityType === 'location'
              ? (() => {
                  const ov = manualOverrides.get(popoverState.rowNumber);
                  if (ov?.customerLocationIds && ov.customerLocationIds.length > 0) {
                    return new Set(ov.customerLocationIds.map((l) => l.id));
                  }
                  return undefined;
                })()
              : undefined
          }
          candidates={popoverState.candidates}
        />
      )}
    </div>
  );
}
